import {useCallback, useRef,} from "react";
import Feature from "ol/Feature";
import {SelectEvent} from "ol/interaction/Select";
import {useTranslation} from "react-i18next";
import {useToast} from "../ui";

export type TMarkerProperty<T = any> = {
  code?: string;
  hovered?: boolean;
  highlighted: boolean;
  disabled?: boolean;
  shipping?: T;
  color?: string;
}

export function useMapFunctions<T = TMarkerProperty>({onSelectShippings}: {
  onSelectShippings: (shippings: T[]) => void
}) {
  const toast = useToast();
  const selectedFeatureRef = useRef<Feature[]>([]);
  const {t} = useTranslation("shipping");
  
  const getSelectedShippings = useCallback(() => {
    const shippings: T[] = [];
    selectedFeatureRef.current && selectedFeatureRef.current.forEach((f) => {
      const properties = f.getProperties() as TMarkerProperty<T>;
      if (properties.shipping) shippings.push(properties.shipping);
    });
    return shippings;
  }, []);
  
  const addToSelectedShippings = useCallback((feature: Feature, properties: TMarkerProperty<T>) => {
    if (!properties?.disabled) {
      selectedFeatureRef.current.push(feature);
      feature.setProperties({...properties, highlighted: true});
    }
  }, []);
  
  const removeFromSelectedShippings = useCallback((feature: Feature, properties: TMarkerProperty<T>) => {
    if (!properties?.disabled) {
      feature.setProperties({...properties, highlighted: false});
      selectedFeatureRef.current = selectedFeatureRef.current.filter((selected) => {
        return selected !== feature;
      });
    }
  }, []);
  
  const isClusterHighlighted = useCallback((features: Feature[]) => {
    let highlighted = false;
    for (let i in features) {
      const f = features[i];
      const properties = f.getProperties() as TMarkerProperty<T>;
      if (properties.highlighted) {
        highlighted = true;
        break;
      }
    }
    return highlighted;
  }, []);
  
  const clickCluster = useCallback((selected: Feature[]) => {
    if (selected?.length) {
      let features: Feature[] = selected[0].get("features");
      if (!features?.length) {
        //is not cluster
        features = selected;
      }
      const highlighted = isClusterHighlighted(features);
      let isWarningShown = false;
      features && features.forEach((f: Feature) => {
        const properties = f.getProperties() as TMarkerProperty<T>;
        if (highlighted) { //클러스터에 선택된 물품이 있을 시 클러스터 속하는 모든 물품 미선택으로 변경
          if (properties.highlighted) removeFromSelectedShippings(f, properties);
        }
        else {
          if (!properties.highlighted) {
            if (properties.disabled) { //분료완료된 물품 선택 불가
              if (!isWarningShown) {
                toast.warning(t("Please, select a shipping not sorted"))
                isWarningShown = true;
              }
            }
            else {
              addToSelectedShippings(f, properties);
            }
          }
        }
      });
      const shippings = getSelectedShippings();
      onSelectShippings(shippings);
    }
  }, [
    addToSelectedShippings,
    isClusterHighlighted,
    getSelectedShippings,
    removeFromSelectedShippings,
    t,
    onSelectShippings
  ]);
  
  const mouseOverCluster = useCallback((selected: Feature[], deselected: Feature[], event: SelectEvent, onMouseOver) => {
    const shippings: T[] = [];
    let selectedFeatures: Feature[] = [];
    if (selected?.length) {
      selectedFeatures = selected[0].get("features");
      if (!selectedFeatures?.length) {
        //is not cluster
        selectedFeatures = selected;
      }
      selectedFeatures && selectedFeatures.forEach((feature) => {
        const properties = feature.getProperties() as TMarkerProperty<T>;
        if (properties.shipping) {
          feature.setProperties({...properties, hovered: true});
          shippings.push(properties.shipping);
        }
        if (shippings.length > 0) {
          onMouseOver({
            shippings, position: event.mapBrowserEvent.coordinate,
          });
        }
      });
    }
    if (deselected?.length) {
      let deselectedFeatures: Feature[] = deselected[0].get("features");
      if (!deselectedFeatures?.length) {
        //is not cluster
        deselectedFeatures = deselected;
      }
      if (selectedFeatures[0] === deselectedFeatures[0]) return;
      onMouseOver();
      deselectedFeatures && deselectedFeatures.forEach((feature) => {
        const properties = feature.getProperties() as TMarkerProperty<T>;
        if (!properties.highlighted) {
          feature.setProperties({...properties, hovered: false});
        }
      });
    }
  }, []);
  
  const resetSelectedShippings = useCallback(() => {
    selectedFeatureRef.current && selectedFeatureRef.current.forEach((feature) => {
      const properties = feature.getProperties() as TMarkerProperty<T>;
      removeFromSelectedShippings(feature, properties);
    });
    onSelectShippings([]);
  }, [removeFromSelectedShippings, onSelectShippings]);
  
  return {
    mouseOverCluster, clickCluster, resetSelectedShippings, addToSelectedShippings, getSelectedShippings,
  }
}
